import { NO_BG_USAGE_KEY } from './constants';

export function computeHasCommitment(row) {
  return (
    row.work_status !== 'done' ||
    row.finance_status !== 'completed_po' ||
    row.legal_warranty ||
    row.legal_debt ||
    row.legal_lawsuit ||
    row.report_status === 'pending' // ต้องส่งรายงานแต่ยังไม่ได้ส่ง = ยังมีภาระผูกพัน
  );
}

export function isBgUsageDisabled(row) {
  return row?.bg_usage_category === NO_BG_USAGE_KEY;
}

// col 12 (P111): BG เหลือที่มีสิทธิจ่าย
// ถ้าเลือก "ไม่ใช้ BG แล้ว" ให้เป็น 0, ไม่งั้นใช้ max(col10, 0)
export function getPayableBgEligible(row) {
  if (isBgUsageDisabled(row)) return 0;
  return Math.max(Number(getProjectedFund(row)) || 0, 0);
}

export function getBgColumnValues(row) {
  const bgUsageDisabled = isBgUsageDisabled(row);
  const col32 = Number(row?.ap_pending_unpaid) || 0;
  const col16 = Number(row?.to_spend) || 0;
  const col12 = getPayableBgEligible(row);
  // col17 (P111 row14) = col10 + col11 + col12
  // = AP รอจ่าย(col32) + ต้องใช้อีก(col16) + BGเหลือที่มีสิทธิจ่าย(col12)
  // (รอจ่าย/col35 ไม่ได้บวกตรงนี้แล้ว — ย้ายไปหักรวมอยู่ใน getProjectedFund/col10 แทน)
  // (รอเงินคืน/col34 ไม่ได้หักตรงนี้แล้ว — ย้ายไปหักใน getPaidValue/BG PAID แทน กันหักซ้ำ)
  const col17 = col32 + col16 + col12;
  const raw = getProjectedFund(row) - col17; // col10 - col17
  // col15 "BG คงเหลือ" = raw เมื่อ >= 0, col14 "เกิน BG" = raw เมื่อติดลบ
  const col15 = Math.max(raw, 0);
  const col14 = raw < 0 ? raw : null;
  return { bgUsageDisabled, col32, col12, col15, col14, col16, col17 };
}

// col 11 "ใช้ไปแล้ว" = ยอด Paid เท่านั้น (expenses ที่ status === 'SUCCESS')
// เหมือนแถวรวมคอลัมน์ "Paid" ใน BudgetTable.js (renderCost) เป๊ะ — BudgetTable รวมยอดผ่าน budget
// ของโปรเจกต์เท่านั้น (ไม่ใช่ expenses ทั้งหมดที่ project ตรงกัน) เลยต้องกรอง e.budget ให้ตรงกับ
// budget จริงของโปรเจกต์นี้ด้วย ไม่งั้นถ้ามี Expense ที่ budget เป็น null/ผูกผิดโปรเจกต์ (data ไม่สมบูรณ์)
// ตัวเลขจะไม่ตรงกับ BudgetTable ทันที (ไล่แก้ data ทีละเคสไม่จบ เพราะเป็นบั๊กที่จุดสร้าง Expense เอง)
// หักด้วยเงินรอคืน (refund_items ยังไม่คืน, col34) — ระหว่างรอคืนเงินก้อนนั้นไม่นับเป็นเงินที่จ่ายไป พอกด
// "ได้รับคืนแล้ว" ก็กลับมานับรวมใน PAID ตามเดิม (ตามที่ user กำหนด 2026-09-24) — ต้องตรงกับ renderCost
// ใน BudgetTable.js เสมอ และห้ามหัก col34 ซ้ำที่อื่นอีก (col17/getSpentValue/realProfit ตัดออกแล้ว)
export function getPaidValue(row) {
  const budgetIds = new Set(
    (row?.budget || []).map((b) => String(b?._id || b?.id)),
  );
  const paidGross = (row?.expenses || []).reduce(
    (acc, e) =>
      acc +
      (e?.status === 'SUCCESS' && budgetIds.has(String(e?.budget))
        ? Number(e?.price) || 0
        : 0),
    0,
  );
  return paidGross - getRefundPendingValue(row);
}

// col 35 "รอจ่าย" = ยอด expense ที่ยังไม่จ่าย/ไม่ถูกปฏิเสธ (status ไม่ใช่ SUCCESS และไม่ใช่ REJECT)
// เหมือนคอลัมน์ "Expected" ใน BudgetTable.js (renderCostApprove) เป๊ะ — กรอง e.budget ให้ตรงกับ budget
// จริงของโปรเจกต์นี้เหมือน getPaidValue ด้านบน (เหตุผลเดียวกัน)
export function getExpectedValue(row) {
  const budgetIds = new Set(
    (row?.budget || []).map((b) => String(b?._id || b?.id)),
  );
  // Expenses ที่ยังมี Budget.pending_items entry ผูกอยู่ (source_expense_id) ถูกนับผ่าน "AP Pending"
  // (ap_pending_unpaid) แทนแล้ว — ข้ามไม่นับซ้ำที่นี่ กันเงินก้อนเดียวกันถูกหักออกจากเงินคงเหลือสองรอบ (ผ่าน
  // Expected/col35 ที่นี่ กับผ่าน AP Pending/col32 พร้อมกัน) ดู upsertLaborExpenseLocked ใน expenses.service.js
  // ที่ดูแล mirror นี้ (สร้าง/แก้ตอนยังเป็น PENDING, ลบทันทีที่สถานะไม่ใช่ PENDING แล้ว — พอลบ mirror ปุ๊บ
  // รายการนี้ก็เริ่มถูกนับตรงนี้แทนเองอัตโนมัติ ไม่มีช่วงที่นับซ้ำหรือขาดหาย) ต้องแก้คู่กับ renderCostApprove
  // ใน BudgetTable.js เสมอ (ของซ้ำกันคนละไฟล์ ดู CLAUDE.md)
  const apPendingLinkedIds = new Set(
    (row?.budget || []).flatMap((b) =>
      (b?.pending_items || [])
        .filter((i) => i?.source_expense_id)
        .map((i) => String(i.source_expense_id)),),
  );
  return (row?.expenses || []).reduce(
    (acc, e) =>
      acc +
      (e?.status !== 'SUCCESS' &&
      e?.status !== 'REJECT' &&
      budgetIds.has(String(e?.budget)) &&
      !apPendingLinkedIds.has(String(e?._id || e?.id))
        ? Number(e?.price) || 0
        : 0),
    0,
  );
}

// col 10 "ทุนคาดการณ์" = col 33 (BG / total_budget) - col 11 (ใช้ไปแล้ว / Paid) - col 35 (รอจ่าย / Expected)
export function getProjectedFund(row) {
  return (
    (Number(row?.total_budget) || 0) - getPaidValue(row) - getExpectedValue(row)
  );
}

// col 34 "รอเงินคืน" = ผลรวม refund_items ที่ยังไม่คืน (refunded !== true) ของทุก budget ในโปรเจกต์นี้
// เหมือนคอลัมน์ "รอเงินคืน" ใน BudgetTable.js (renderRefundPending) รวมทุก budget
export function getRefundPendingValue(row) {
  return (row?.budget || []).reduce((acc, b) => {
    const unrefunded = (b?.refund_items || []).filter((i) => !i?.refunded);
    return (
      acc + unrefunded.reduce((s, i) => s + (Number(i?.amount) || 0), 0)
    );
  }, 0);
}

// ใช้ไปจริง (Paid + Expected สด จาก getPaidValue/getExpectedValue) + AP รอจ่าย (pending_items ยังไม่จ่าย)
// + ต้องใช้อีก(to_spend) + BGเหลือที่มีสิทธิจ่าย(payableBgEligible, col12)
// (เงินรอคืน refund_items ถูกหักอยู่ใน getPaidValue แล้ว ไม่หักซ้ำตรงนี้)
// = สูตรเดียวกับ realProfit ใน ProjectSingleDashboard.js (T-1) — เดิมใช้ field ดิบ seed_ap_pending
// (AP ตั้งต้นจากการ seed ข้อมูลโปรเจกต์เก่า) ตรงนี้แทน ทำให้ตัวเลขไม่ตรงกับ T-1 ตอน BG ยังเปิดใช้อยู่
// (ปิดใช้ BG แล้วทั้งคู่ = 0 เหมือนกันอยู่แล้วเลยไม่โดน) เปลี่ยนมาใช้ payableBgEligible ให้ตรงกับ T-1
// ตามที่ user ยืนยัน 2026-08-06 — ใช้เป็นส่วนประกอบของ col 19 "กำไรคาดการณ์" (getEstimatedProfit
// ด้านล่าง) เท่านั้น ไม่ใช่ col 11 แล้ว
function getSpentValue(row) {
  return (
    getPaidValue(row) +
    getExpectedValue(row) +
    (Number(row?.ap_pending_unpaid) || 0) +
    (Number(row?.to_spend) || 0) +
    getPayableBgEligible(row)
  );
}

// col 19 "กำไรคาดการณ์" — สูตรเดียวกับ row22Profit ใน ProjectSingleDashboard.js (T-1):
// ปกติ = po (หัก VAT ถ้า include_vat, ใช้ getPoNetOfVat) - จ่ายแล้ว(getSpentValue)
// (เดิมตอนปิดใช้ BG แล้ว เคยบวกผลรวมงบเหลือเป็นบวกของแต่ละ budget (positiveRemainingSum) กลับเข้าไปเป็น
// กำไรเพิ่ม ทำให้ P5/T-1 ตัวเลขไม่ตรงกัน — ตัดออกแล้ว, เดิมใช้ row.cost ดิบไม่หัก VAT ด้วย — เปลี่ยนเป็น
// getPoNetOfVat ให้ตรงกับ T-1 ตามที่ user ยืนยัน 2026-08-07)
// ถ้าโปรเจกต์ถูกฟ้องร้องแล้ว (legal_lawsuit) งานหยุดชะงัก ใช้สูตรง่ายๆ รับเงินมาแล้ว(received+seed_received)
// - จ่ายไปแล้ว(getPaidValue) แทน ไม่นับส่วนที่ยังไม่เกิดขึ้นจริง (รอจ่าย/ต้องใช้อีก/ฯลฯ) — เหมือน T-1
// เป๊ะ (เดิม P5 ไม่มีเงื่อนไขนี้เลย เพิ่งเพิ่มพร้อมกับจุด VAT ด้านบน 2026-08-07)
export function getEstimatedProfit(row) {
  if (row?.legal_lawsuit) {
    const receivedCombined =
      (Number(row?.received) || 0) + (Number(row?.seed_received) || 0);
    return receivedCombined - getPaidValue(row);
  }
  return getPoNetOfVat(row) - getSpentValue(row);
}

// col 18 "Profit Target" — สูตรเดียวกับ targetProfit ใน ProjectSingleDashboard.js (P111 num 5) = cost - BG ที่ตั้งไว้
export function getTargetProfit(row) {
  return (Number(row?.cost) || 0) - (Number(row?.total_budget) || 0);
}

// PO (col 6) หัก VAT ถ้า include_vat — สูตรกลางที่ใช้ร่วมกันทั้ง filter/sort/totals/Excel export/render
export function getPoNetOfVat(row) {
  return row?.include_vat
    ? (row?.cost || 0) - (row?.vat || 0)
    : row?.cost || 0;
}
